import { BrandZohoTypes, ElithairZohoSchema, ElithairZohoTypes } from "@elit-hair/brands";
import { PaginatedResponse } from "@elit-hair/elit-tools";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { callSvcAxiosInstance } from "@/constants";
import { CALL_STATUS, CALL_TYPES } from "@/constants/call";
import { callServiceEndpoints } from "@/constants/endpoints";
import { DEFAULT_CRM_LANGUAGE } from "@/constants/locale";
import { DEFAULT_EMPTY_PAGINATION, PAGINATION } from "@/constants/pagination";
import { createQueryKey } from "@/constants/query-keys";
import { CallDimensionFilters } from "@/types/call-overall-score-filter.types";
import {
    Call,
    CallAnsweredByType,
    CallDirection,
    CallPayload,
    KpiData,
    TeamMembersKpiResponse,
} from "@/types/call.types";
import { LeadLanguageFilter } from "@/types/lead-language.types";

export type GetCallsParams = {
    page?: number;
    userId?: string;
    userIds?: string[];
    search?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    answeredByList?: CallAnsweredByType[];
    callDurationMin?: number;
    callDurationMax?: number;
    dimensionDefinitionId?: string;
    dimensionFilters?: CallDimensionFilters;
    signal?: AbortSignal;
    appointmentId?: string;
};

const UI_TO_CRM_LANGUAGE_MAP: Record<string, LeadLanguageFilter> = {
    en: "ENG",
    uk: "ENG",
    es: "ES",
    de: "DE",
    dach: "DE",
    tr: "TR",
    it: "ITA",
    ar: "AR",
    prt: "PRT",
    fr: "FR",
    hr: "HR",
    us: "US",
    prp: "PRP",
};

export const mapLanguageCodeToCRMLeadLanguage = (languageCode: string): LeadLanguageFilter => {
    const crmLanguages = ElithairZohoSchema["Leads"]?.["Sprache"]?.enum || [];

    if (UI_TO_CRM_LANGUAGE_MAP[languageCode]) {
        return UI_TO_CRM_LANGUAGE_MAP[languageCode];
    }

    const upperCode = languageCode.toUpperCase();
    if (upperCode === "PRP") {
        return "PRP";
    }

    const directMatch = crmLanguages.find((lang) => String(lang).toUpperCase() === upperCode) as
        | ElithairZohoTypes["Leads"]["Sprache"]
        | undefined;
    if (directMatch) {
        return directMatch;
    }

    return DEFAULT_CRM_LANGUAGE;
};

export const callKeys = {
    all: createQueryKey.calls(),
    lists: () => createQueryKey.callLists(),
    list: (filters: Record<string, unknown>) => createQueryKey.callList(filters),
    details: () => createQueryKey.callDetails(),
    detail: (id: string) => createQueryKey.callDetail(id),
    allCalls: () => createQueryKey.allCalls(),
    leads: (status?: BrandZohoTypes["Leads"]["Lead_Status"], language?: LeadLanguageFilter) =>
        ["leads", status, language] as const,
};

const getCallsByUser = async (
    page: number = DEFAULT_EMPTY_PAGINATION.page,
    userId?: string
): Promise<PaginatedResponse<Call>> => {
    try {
        const response = await callSvcAxiosInstance.get(callServiceEndpoints.getCallsByUser, {
            params: {
                page,
                limit: PAGINATION.PAGE_SIZE,
                ...(userId ? { userIds: [userId] } : {}),
            },
        });
        return response.data || { data: [], pagination: DEFAULT_EMPTY_PAGINATION };
    } catch (error) {
        console.error("Failed to fetch calls:", error);
        return { data: [], pagination: DEFAULT_EMPTY_PAGINATION };
    }
};

const getCalls = async ({
    page = DEFAULT_EMPTY_PAGINATION.page,
    userId,
    userIds,
    search,
    startDate,
    endDate,
    limit = PAGINATION.PAGE_SIZE,
    answeredByList,
    callDurationMin,
    callDurationMax,
    dimensionDefinitionId,
    dimensionFilters,
    signal,
    appointmentId,
}: GetCallsParams = {}): Promise<PaginatedResponse<Call>> => {
    try {
        const hasDimensionFilters = dimensionFilters?.minValue != null || dimensionFilters?.maxValue != null;
        const resolvedUserIds = userIds ?? (userId ? [userId] : undefined);

        const response = await callSvcAxiosInstance.get(callServiceEndpoints.getCallsByUser, {
            signal,
            params: {
                page,
                limit,
                ...(resolvedUserIds ? { userIds: resolvedUserIds } : {}),
                ...(search ? { searchTerm: search } : {}),
                ...(startDate ? { startDate } : {}),
                ...(endDate ? { endDate } : {}),
                ...(answeredByList && answeredByList.length > 0 ? { answeredByList } : {}),
                ...(callDurationMin != null ? { callDurationMin } : {}),
                ...(callDurationMax != null ? { callDurationMax } : {}),
                ...(dimensionDefinitionId && hasDimensionFilters ? { dimensionDefinitionId, dimensionFilters } : {}),
                ...(appointmentId ? { appointmentId } : {}),
            },
        });

        return response.data || { data: [], pagination: DEFAULT_EMPTY_PAGINATION };
    } catch (error) {
        // Let React Query cancel superseded searches instead of treating aborts as empty results
        if (axios.isCancel(error) || (axios.isAxiosError(error) && error.code === "ERR_CANCELED")) {
            throw error;
        }

        console.log("Failed to fetch calls by filter:", error);
        return { data: [], pagination: DEFAULT_EMPTY_PAGINATION };
    }
};

const getCall = async (callId: string): Promise<Call> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getCall(callId));
    return response.data;
};

const createCall = async (callData: Partial<CallPayload>): Promise<Call> => {
    const sanitizedCalledName = callData.calledName?.trim();
    const sanitizedCalledEmail = callData.calledEmail?.trim();

    const payload = {
        callerNumber: callData.From,
        calledNumber: callData.To,
        orgId: callData.orgId,
        moduleType: callData.moduleType,
        recordId: callData.recordId,
        appointmentId: callData.appointmentId,
        callPurpose: callData.callPurpose,
        ...(sanitizedCalledName ? { calledName: sanitizedCalledName } : {}),
        ...(sanitizedCalledEmail ? { calledEmail: sanitizedCalledEmail } : {}),
    };

    const response = await callSvcAxiosInstance.post(callServiceEndpoints.createCall, payload);

    return response.data;
};

const getKpiByDate = async (date: string): Promise<KpiData | null> => {
    try {
        const response = await callSvcAxiosInstance.get(callServiceEndpoints.getKpiByDate(date));
        return response.data || null;
    } catch (error) {
        console.error(`Failed to fetch KPI for date ${date}:`, error);
        return null;
    }
};

const getKpiByDateRange = async (
    start: DateTime,
    end: DateTime,
    userId?: string,
    callPurpose?: string
): Promise<Record<string, KpiData>> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getKpiByDateRange, {
        params: {
            start: start.toFormat("yyyy-MM-dd"),
            end: end.toFormat("yyyy-MM-dd"),
            timezoneOffset: DateTime.now().offset,
            ...(userId ? { userId } : {}),
            ...(callPurpose ? { callPurpose } : {}),
        },
    });
    return response.data || {};
};

const getTeamMembersKpiByDate = async (date: string, teamId?: string): Promise<TeamMembersKpiResponse | null> => {
    try {
        const params = teamId ? { teamId } : {};
        const response = await callSvcAxiosInstance.get(callServiceEndpoints.getTeamMembersKpiByDate(date), {
            params,
        });
        return response.data || null;
    } catch (error) {
        console.error(`Failed to fetch team members KPI for date ${date}:`, error);
        return null;
    }
};

const getTeamMembersKpiByDateRange = async (
    start: DateTime,
    end: DateTime,
    teamId?: string,
    callPurpose?: string
): Promise<Record<string, TeamMembersKpiResponse>> => {
    const params: Record<string, string | number> = {
        start: start.toFormat("yyyy-MM-dd"),
        end: end.toFormat("yyyy-MM-dd"),
        timezoneOffset: DateTime.now().offset,
    };

    if (teamId) {
        params.teamId = teamId;
    }

    if (callPurpose) {
        params.callPurpose = callPurpose;
    }

    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getTeamMembersKpiByDateRange, { params });
    return response.data || {};
};

const getLeadById = async (leadId: string, brandName: string): Promise<BrandZohoTypes["Leads"]> => {
    const response = await callSvcAxiosInstance.get<BrandZohoTypes["Leads"]>(callServiceEndpoints.getLeadById(leadId), {
        params: {
            brandName,
        },
    });

    // Return raw API data without mapping
    return response.data;
};

const getContactById = async (contactId: string, brandName: string): Promise<BrandZohoTypes["Contacts"]> => {
    const response = await callSvcAxiosInstance.get<BrandZohoTypes["Contacts"]>(
        callServiceEndpoints.getContactById(contactId),
        {
            params: {
                brandName,
            },
        }
    );

    // Return raw API data without mapping
    return response.data;
};

export type LeadWithAssignment = {
    id: string;
    zohoLeadId: string;
    orgId: string;
    email: ElithairZohoTypes["Leads"]["Email"];
    phone: ElithairZohoTypes["Leads"]["Phone"];
    mobile: ElithairZohoTypes["Leads"]["Mobile"];
    settingPriority: number | null;
    assigneeId: string | null;
    status: ElithairZohoTypes["Leads"]["Lead_Status"];
    fullName: ElithairZohoTypes["Leads"]["Full_Name"];
    salutation?: string;
    image1: string | null;
    image2: string | null;
    image3: string | null;
    createdAt: DateTime;
    updatedAt: DateTime;
    assignmentExpiresAt?: DateTime | null;
};

type LeadWithAssignmentRaw = Omit<
    LeadWithAssignment,
    "createdAt" | "updatedAt" | "assignmentExpiresAt" | "settingPriority"
> & {
    createdAt: string;
    updatedAt: string;
    assignmentExpiresAt?: string | null;
    settingPriority?: number | null;
};

const getAndAssignLeads = async (
    status: BrandZohoTypes["Leads"]["Lead_Status"],
    language: LeadLanguageFilter
): Promise<LeadWithAssignment[]> => {
    const response = await callSvcAxiosInstance.post<LeadWithAssignmentRaw[]>(callServiceEndpoints.getAndAssignLeads, {
        languages: [language],
        ...(String(status) !== "-None-" && { statuses: [String(status)] }),
    });

    // Transform dates to Luxon DateTime objects (backend always returns ISO strings)
    return response.data.map((lead): LeadWithAssignment => {
        return {
            ...lead,
            settingPriority: lead.settingPriority ?? null,
            createdAt: DateTime.fromISO(lead.createdAt),
            updatedAt: DateTime.fromISO(lead.updatedAt),
            assignmentExpiresAt: lead.assignmentExpiresAt ? DateTime.fromISO(lead.assignmentExpiresAt) : null,
        };
    });
};

const unassignLeads = async (): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.unassignLeads);
};

const getUserKpiByDate = async (date: string, userId: string, timezoneOffset: number = 0): Promise<KpiData | null> => {
    try {
        const response = await callSvcAxiosInstance.get(callServiceEndpoints.getUserKpiByDate(date), {
            params: { userId, timezoneOffset },
        });
        return response.data || null;
    } catch (error) {
        console.error(`Failed to fetch KPI for user ${userId} on date ${date}:`, error);
        return null;
    }
};

// Get all calls for a single day (paginated under the hood; backend caps limit at 100 per page)
const ANALYTICS_PAGE_SIZE = 100;

const getAllCallsForDay = async (date: string, userId?: string): Promise<Call[]> => {
    const calls: Call[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
        const response = await getCalls({
            page,
            userId,
            startDate: date,
            endDate: date,
            limit: ANALYTICS_PAGE_SIZE,
        });
        calls.push(...response.data);
        hasMore = response.pagination.hasMore;
        page += 1;
    }

    return calls;
};

const getAllCallsForTeamDay = async (date: string, userIds: string[]): Promise<Call[]> => {
    if (userIds.length === 0) {
        return [];
    }

    const calls: Call[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
        const response = await getCalls({
            page,
            userIds,
            startDate: date,
            endDate: date,
            limit: ANALYTICS_PAGE_SIZE,
        });

        calls.push(...response.data);

        hasMore = response.pagination.hasMore;
        page += 1;
    }

    return calls;
};

export const useTeamCallsForDay = (date: string, userIds: string[], enabled = true) => {
    return useQuery({
        queryKey: ["teamCallsForDay", date, userIds],
        queryFn: () => getAllCallsForTeamDay(date, userIds),
        enabled: enabled && !!date && userIds.length > 0,
    });
};

export const useCallsForAnalytics = (date: string, userId?: string) => {
    return useQuery({
        queryKey: ["callsForAnalytics", date, userId],
        queryFn: () => getAllCallsForDay(date, userId),
        enabled: !!date,
    });
};

// Get paginated calls for current user
export const useCallsPaginated = ({
    page = PAGINATION.DEFAULT_PAGE,
    userId,
    search,
    startDate,
    endDate,
    limit,
    answeredByList,
    callDurationMin,
    callDurationMax,
    dimensionDefinitionId,
    dimensionFilters,
}: GetCallsParams = {}) => {
    const defaultCallsResponse: PaginatedResponse<Call> = {
        data: [],
        pagination: DEFAULT_EMPTY_PAGINATION,
    };

    const query = useQuery({
        queryKey: callKeys.list({
            page,
            userId,
            search,
            startDate,
            endDate,
            limit,
            answeredByList,
            callDurationMin,
            callDurationMax,
            dimensionDefinitionId,
            dimensionFilters,
        }),
        queryFn: ({ signal }) =>
            getCalls({
                page,
                userId,
                search,
                startDate,
                endDate,
                limit,
                answeredByList,
                callDurationMin,
                callDurationMax,
                dimensionDefinitionId,
                dimensionFilters,
                signal,
            }),
    });

    return {
        ...query,
        data: query.data ?? defaultCallsResponse,
    };
};

// Get a single call by ID
export const useCallQuery = (callId: string | undefined) => {
    return useQuery<Call, Error>({
        queryKey: callKeys.detail(callId ?? ""),
        queryFn: () => {
            if (!callId) throw new Error("No callId provided");
            return getCall(callId);
        },
        enabled: !!callId,
        staleTime: 5 * 1000,
    });
};

// Utility functions
export const getLastStatus = (call: Call): string => {
    if (!call.statusLogs || call.statusLogs.length === 0) {
        return "unknown";
    }

    return call.statusLogs[call.statusLogs.length - 1].status;
};

export const getCallType = (call: Call): CallDirection => {
    // Check if the call is answered
    const hasInProgress = call.statusLogs.some((log) => log.status === CALL_STATUS.IN_PROGRESS);

    if (!hasInProgress && call.status === CALL_STATUS.COMPLETED) {
        return CALL_TYPES.MISSED; // Call is not answered but completed (missed)
    }

    // Check if the user's number is calling
    // Note: This logic can be changed depending on What we want to do
    return CALL_TYPES.INCOMING;
};

// Get active call for a specific user (for agent information panel)
export const useActiveCallForUser = (userId: string | undefined) => {
    const query = useQuery({
        queryKey: callKeys.list({ page: 1, userId, limit: 1 }),
        queryFn: () => getCallsByUser(1, userId), // Get calls for specific user only
        enabled: !!userId, // Only fetch if userId is provided
    });

    // Find active call for the specific user
    const activeCall = useMemo(() => {
        if (!query.data?.data || !userId) return null;

        // Get the latest call and check if it's in progress
        const latestCall = query.data.data[0];
        if (!latestCall) return null;

        // Check if the latest call is in progress
        const hasInProgress = latestCall.statusLogs?.some((log) => log.status === CALL_STATUS.IN_PROGRESS);
        const isActive = hasInProgress && latestCall.status === CALL_STATUS.IN_PROGRESS;

        const userActiveCall = isActive ? latestCall : null;

        return userActiveCall;
    }, [query.data?.data, userId]);

    return {
        ...query,
        activeCall,
    };
};

export const useLeads = (status: BrandZohoTypes["Leads"]["Lead_Status"] | undefined, languageCode?: string) => {
    const { i18n } = useTranslation();
    const language = useMemo(
        () => mapLanguageCodeToCRMLeadLanguage(languageCode ?? i18n.language),
        [languageCode, i18n.language]
    );

    return useQuery({
        queryKey: callKeys.leads(status, language),
        queryFn: () => {
            if (!status) throw new Error("Status is required");
            return getAndAssignLeads(status, language);
        },
        enabled: !!status,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
    });
};

export const useUnassignLeads = () => {
    return useMutation({
        mutationFn: unassignLeads,
    });
};

// Export service functions for direct use
export {
    getCalls,
    getCallsByUser,
    getCall,
    createCall,
    getKpiByDate,
    getKpiByDateRange,
    getTeamMembersKpiByDate,
    getTeamMembersKpiByDateRange,
    getLeadById,
    getContactById,
    getUserKpiByDate,
    getAndAssignLeads,
    unassignLeads,
};
